import styles from '../../checkout.module.css';

import { Cart, CartItem, RentalCartItem, SaleCartItem } from '../../../../clients/customer-api/cart/cart';
import { Button } from '../../../../components/form/button';
import { Typography } from '../../../../components/typography/typography';
import { OrderType } from '../../../../clients/quipli-service/order/order';
import {
  ExcludeProductFeeRequest,
  LineItem,
  ProductFeeLineItem,
} from '../../../../clients/quipli-service/financials/financials';
import { ProductCustomService } from '../../../../clients/customer-api/product/customer-product';
import { ProductOption } from '../../../../clients/customer-api/product/customer-product';
import { Fragment } from 'react/jsx-runtime';
import classNames from 'classnames';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import { useCustomerAPI } from '../../../../clients/customer-api/customer-api-client';
import { Location } from '../../../../clients/quipli-service/location/location';
import { FieldCounter } from '../../../../components/field/field';
import Icon from '../../../../components/icons/icon';
import { useCustomerYorentClient } from '../../../../clients/customer-api/customer-yorent-api-client';
import { ecommerceToast } from '../../../../util/toasts';
import { ProductTrackingType } from '../../../../clients/quipli-service/product/product';
import { displayPriceLabel } from '../utils/summary-utils';
import { ConfirmModal } from '../../../../components/modal/confirm-modal';
import { TaxBreakdown } from '../../../../clients/quipli-service/tax/tax';

export const CartItemSummary = ({
  item,
  lineItem,
  fees,
  cart,
  location,
  reload,
  taxInclusive,
  taxLabel,
  taxBreakdown,
  customizations,
  isSubmitting,
  setExcludedProductFees,
  excludeProductFees,
  getDuration,
  isQuote,
  setModal,
}: {
  item: CartItem;
  lineItem: LineItem | undefined;
  fees: ProductFeeLineItem[] | undefined;
  cart: Cart;
  location: Location;
  reload: () => void;
  customizations?: Map<number, { options: ProductOption[]; services: ProductCustomService[] }>;
  taxInclusive: boolean;
  taxLabel: string;
  taxBreakdown: TaxBreakdown[];
  isSubmitting: boolean;
  setExcludedProductFees?: (excludedProductFees: ExcludeProductFeeRequest[]) => void;
  excludeProductFees?: ExcludeProductFeeRequest[];
  getDuration: (rentalItem: RentalCartItem) => string | undefined;
  isQuote: boolean | undefined;
  setModal: (modal: React.ReactNode) => void;
}) => {
  const { updateCartItem, updateCartMeta, removeItemFromCart } = useCustomerAPI();
  const { refreshCartProductCount } = useCustomerYorentClient();

  return (
    <div key={`product-${item.id}`} className={styles.cartItemWrapper}>
      <div className={styles.cartItemInfo}>
        <Typography type="text-xs" weight="semi-bold">
          {item.product.name || lineItem?.description}
        </Typography>

        {item.type === OrderType.Rental && (item as RentalCartItem).endDate && (
          <Typography type="text-xs" className="text-quipli-black-700">
            Duration: {getDuration(item as RentalCartItem)}
          </Typography>
        )}

        {/* Options */}
        {Object.entries(item.options).map(([optionId, valueId]) => {
          const option = customizations?.get(item.product.id)?.options.find((o) => o.id === +optionId);
          const name = option?.name;
          const value = option?.values?.find((v) => v.id === +valueId)?.name;

          return (
            name &&
            value && (
              <Fragment key={`product-${item.id}-option-${name}`}>
                <Typography type="text-xs" className="text-quipli-black-700">
                  {name}: {value}
                </Typography>
              </Fragment>
            )
          );
        })}

        {/* Cargo */}
        {item instanceof SaleCartItem && (
          <div className="flex flex-col gap-1">
            <Typography type="text-xs" weight="semi-bold" className="text-quipli-black-500">
              Cargo Requirements:
            </Typography>

            <ul>
              {item.services.map(({ id, value }) => {
                const service = customizations?.get(item.product.id)?.services.find((s) => s.id === id);
                const options = service?.options;

                return (
                  <li key={`product-${item.id}-service-${id}`}>
                    <div className="flex gap-1 ml-2">
                      <Typography
                        type="text-xs"
                        className="text-quipli-black-700"
                        dataCy={`cart-item-service-name-${id}`}
                      >
                        - {service?.name}
                        {options ? ':' : ''}
                      </Typography>
                      <Typography
                        type="text-xs"
                        weight={options ? 'regular' : 'semi-bold'}
                        className={classNames('text-quipli-black-700', {
                          '!italic': !!options,
                        })}
                      >
                        {options ? value : `(${value})`}
                      </Typography>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Product fees */}
        {fees?.map((fee) => (
          <div key={`product-${item.id}-fee-${fee.productFeeId}`} className="flex">
            {fee.optional && (
              <Checkbox
                type="ecommercePrimary"
                isChecked={
                  !excludeProductFees?.find(
                    (pf) => pf.productFeeId === fee.productFeeId && pf.productLineItemId === item.id,
                  )
                }
                isDisabled={isSubmitting}
                onChange={(isChecked) => {
                  if (isChecked) {
                    setExcludedProductFees?.(
                      excludeProductFees?.filter(
                        (pf) => pf.productFeeId !== fee.productFeeId || pf.productLineItemId !== item.id,
                      ) || [],
                    );
                    updateCartMeta(
                      {
                        ...cart.meta,
                        excludeProductFees: excludeProductFees?.filter(
                          (pf) => pf.productFeeId !== fee.productFeeId || pf.productLineItemId !== item.id,
                        ),
                      },
                      location.id,
                    );
                  } else {
                    setExcludedProductFees?.([
                      ...(excludeProductFees || []),
                      {
                        productFeeId: fee.productFeeId,
                        productLineItemId: item.id,
                        productId: item.product.id,
                      },
                    ]);
                    updateCartMeta(
                      {
                        ...cart.meta,
                        excludeProductFees: [
                          ...(excludeProductFees || []),
                          {
                            productFeeId: fee.productFeeId,
                            productLineItemId: item.id,
                            productId: item.product.id,
                          },
                        ],
                      },
                      location.id,
                    );
                  }
                }}
              />
            )}

            <div className="flex gap-1">
              <Typography type="text-xs" weight="semi-bold" className="text-quipli-black-500">
                {fee.description}:
              </Typography>
              <Typography type="text-xs" className="text-quipli-black-500">
                {`${fee.amount.toFormat()}${fee.optional ? ' (optional)' : ''}`}
              </Typography>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.cartItemQty}>
        <Icon icon="Close" size="small" stroke="var(--quipli-black-800)" />

        <FieldCounter
          hideButtons
          value={item.quantity}
          min={1}
          dataCy={`cart-item-quantity`}
          disabled={isSubmitting || isQuote}
          onChange={async (newQty) => {
            if (isQuote) return;

            try {
              await updateCartItem(
                {
                  ...item,
                  quantity: newQty,
                },
                item.product && item.product.trackingType === ProductTrackingType.NonInventory ? true : undefined,
                location.id,
              );
              await refreshCartProductCount();

              ecommerceToast('Item quantity updated');
              reload();
            } catch (e: any) {
              ecommerceToast(e.message || 'Error updating item quantity', 'error');
              reload();
            }
          }}
        />
      </div>

      <div
        className={classNames(styles.cartItemPriceAction, {
          '!justify-end': isQuote,
        })}
      >
        <Typography type="text-sm">
          {displayPriceLabel(item, taxInclusive, location, taxLabel, lineItem, taxBreakdown)}
        </Typography>

        {!isQuote && (
          <Button
            className="icon-button !p-0 !m-0"
            dataCy={`cart-item-remove-button`}
            onClick={() =>
              !isSubmitting &&
              setModal(
                <ConfirmModal
                  prompt="Remove item?"
                  additionalText="This cannot be undone"
                  noLabel="Cancel"
                  yesLabel="Remove"
                  yesButtonYellow
                  isOpen
                  useEcommerceTheme
                  headerIcon={
                    <div className="flex justify-center">
                      <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                    </div>
                  }
                  onConfirm={async () => {
                    await removeItemFromCart(item.id, location.id);
                    await refreshCartProductCount();
                    setModal(undefined);
                    reload();
                  }}
                  close={() => setModal(undefined)}
                />,
              )
            }
          >
            <Icon icon="Trash" stroke="var(--grey-400)" />
          </Button>
        )}
      </div>
    </div>
  );
};
